import useUserPermissions from "@/shared/model/user/hooks/useUserPermissions.ts";
import {
  RH_PLANNING_BROUILLON_VISU,
  RH_PLANNING_EQUIVALENCE_HORAIRE_MODIF,
  RH_PLANNING_PREVISIONNEL_MODIF,
  RH_PLANNING_PRINT,
  RH_PLANNING_PUBLIER,
  RH_PLANNING_REEL_MODIF_VAL_PROVISOIRE,
  RH_PLANNING_TEMPLATE_SITE_MODIF,
  RH_PLANNING_TEMPS_PAUSE_MODIF,
  RH_PLANNING_VISU,
  RH_ETAT_CIVIL_VISU,
  RH_TEMPS_PREFERENCES,
  RH_AFFECTATION_VISU,
  RH_AFFECTATION_SALAIRES_VISU,
} from "@/shared/model/user/permissions/Permissions.ts";
import { useParams } from "react-router-dom";
import type { AchievedScheduleValidationLevel } from "@/pages/human-resources/time-management/business/schedule/AchievedScheduleValidationLevel.ts";
import { useCallback, useMemo } from "react";
import { MyCawanModule } from "@/shared/model/modules/MyCawanModule.ts";

interface ScheduleUserPermissions {
  canView: boolean;
  canViewDraft: boolean;
  canEditSchedule: boolean;
  canEditScheduleWithAchievedLevel: (
    level: AchievedScheduleValidationLevel,
  ) => boolean;
  canEditTemporaryValidatedAchievedSchedule: boolean;
  canValidate: boolean;
  canEditBreakDuration: boolean;
  canOverrideDailyTagDuration: boolean;
  canPrint: boolean;
  canEditSiteTemplates: boolean;
  canSeeEmployeePersonalData: boolean;
  canSeeEmployeeContracts: boolean;
  canSeeSalaries: boolean;
  canEditSchedulePreferences: boolean;
  visibleSitesIds: Set<string>;
}

export default function useScheduleUserPermissions(): ScheduleUserPermissions {
  const { domainId, siteId } = useParams();
  const userPermissions = useUserPermissions();

  const canAccessSite =
    siteId !== undefined && userPermissions.canAccessSiteId(siteId);

  const allowed = (required: string[]): boolean =>
    canAccessSite &&
    domainId !== undefined &&
    (userPermissions.ofDomainId(domainId)?.hasEveryPermissions(required) ??
      false);

  const canEditSchedule = allowed([RH_PLANNING_PREVISIONNEL_MODIF]);
  const canEditTemporaryValidatedAchievedSchedule = allowed([
    RH_PLANNING_REEL_MODIF_VAL_PROVISOIRE,
  ]);

  const canEditScheduleWithAchievedLevel = useCallback(
    (level: AchievedScheduleValidationLevel) => {
      if (!canEditSchedule) return false;
      return (
        level === "NONE" ||
        (level === "TEMPORARY" && canEditTemporaryValidatedAchievedSchedule)
      );
    },
    [canEditSchedule, canEditTemporaryValidatedAchievedSchedule],
  );

  const visibleSitesIds = useMemo(
    () =>
      new Set<string>(
        userPermissions
          .domainsWithPermission(RH_PLANNING_VISU)
          .flatMap((d) => d.sites)
          .filter((s) => s.hasModule(MyCawanModule.RH_GESTION_TEMPS))
          .map((s) => s.id),
      ),
    [userPermissions],
  );

  return {
    canView: allowed([RH_PLANNING_VISU]),
    canViewDraft: allowed([RH_PLANNING_VISU, RH_PLANNING_BROUILLON_VISU]),
    canEditSchedule,
    canEditTemporaryValidatedAchievedSchedule,
    canEditScheduleWithAchievedLevel,
    canValidate: allowed([RH_PLANNING_PUBLIER]), // This is really for validation (despite the constant name) The permission to publish is RH_PLANNING_ENVOI_EMAIL
    canEditBreakDuration: allowed([
      RH_PLANNING_PREVISIONNEL_MODIF,
      RH_PLANNING_TEMPS_PAUSE_MODIF,
    ]),
    canOverrideDailyTagDuration: allowed([
      RH_PLANNING_PREVISIONNEL_MODIF,
      RH_PLANNING_EQUIVALENCE_HORAIRE_MODIF,
    ]),
    canPrint: allowed([RH_PLANNING_PRINT]),
    canEditSiteTemplates: allowed([RH_PLANNING_TEMPLATE_SITE_MODIF]),
    canSeeEmployeePersonalData: allowed([RH_ETAT_CIVIL_VISU]),
    canSeeEmployeeContracts: allowed([RH_AFFECTATION_VISU]),
    canSeeSalaries: allowed([RH_AFFECTATION_SALAIRES_VISU]),
    canEditSchedulePreferences: allowed([RH_TEMPS_PREFERENCES]),
    visibleSitesIds,
  };
}
